import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, ShieldCheck, X } from "lucide-react";
import LightPayMark from "@/components/LightPayMark";
import PinKeypad from "@/components/PinKeypad";
import AmbientField from "@/components/AmbientField";
import { errorCode, retryAfterSeconds, setUnlockTicket } from "@/lib/appLock";

const hapticNotify = (type) => {
  try {
    window?.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.(type);
  } catch {
    /* noop */
  }
};

const PIN_LENGTH = 4;

const Dots = ({ filled, shaking, error }) => (
  <div className={`flex items-center justify-center gap-4 ${shaking ? "pin-dots-shake" : ""}`}>
    {Array.from({ length: PIN_LENGTH }, (_, i) => (
      <span
        key={i}
        className={`h-3.5 w-3.5 rounded-full transition-colors duration-150 ${error ? "bg-destructive" : i < filled ? "bg-primary" : "bg-[var(--lp-tile)]"
        }`}
      />
    ))}
  </div>
);

/**
 * Полноэкранный флоу создания/смены/отключения кода-пароля.
 * mode: 'create' | 'change' | 'disable'
 */
const PinSetupSheet = ({ api, user, mode = "create", onClose, onSuccess, onLinkEmail }) => {
  const { t } = useTranslation();

  // step: current | enter | confirm | done
  const [step, setStep] = useState(mode === "create" ? "enter" : "current");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [lockSeconds, setLockSeconds] = useState(0);
  const currentPinRef = useRef("");
  const newPinRef = useRef("");

  const linkedEmail = String(user?.email || "").trim();

  const shake = () => {
    setShaking(true);
    setTimeout(() => setShaking(false), 450);
  };

  const fail = (message) => {
    hapticNotify("error");
    shake();
    setValue("");
    setErrorText(message);
  };

  const handleApiError = (err, fallback) => {
    const code = errorCode(err);
    const retry = retryAfterSeconds(err);
    if (code === "pin_locked" || retry > 0) {
      setValue("");
      setLockSeconds(retry);
      fail(t("security.locked_for", { seconds: retry }));
      return;
    }
    if (code === "invalid_pin") {
      const failsLeft = Number(err?.response?.data?.detail?.fails_left);
      fail(
        Number.isFinite(failsLeft) && failsLeft > 0
          ? t("security.wrong_pin_left", { count: failsLeft })
          : t("security.wrong_pin")
      );
      // Ошиблись в текущем коде — начинаем с него заново.
      if (mode !== "create") setStep("current");
      return;
    }
    if (!err?.response) {
      fail(t("security.network_error"));
      return;
    }
    fail(fallback);
  };

  const submit = useCallback(
    async (pin) => {
      setBusy(true);
      setErrorText("");
      try {
        if (step === "current") {
          if (mode === "disable") {
            await api.post("/security/pin/disable", { pin });
            hapticNotify("success");
            onSuccess?.("disable");
            return;
          }
          // Смена: текущий код проверится на бэке вместе с новым — здесь просто запоминаем.
          currentPinRef.current = pin;
          setValue("");
          setStep("enter");
          return;
        }
        if (step === "enter") {
          newPinRef.current = pin;
          setValue("");
          setStep("confirm");
          return;
        }
        if (step === "confirm") {
          if (pin !== newPinRef.current) {
            newPinRef.current = "";
            setStep("enter");
            fail(t("security.pins_mismatch"));
            return;
          }
          const payload = { pin };
          if (mode === "change") payload.current_pin = currentPinRef.current;
          const res = await api.post("/security/pin/set", payload);
          if (res.data?.unlock_ticket) setUnlockTicket(res.data.unlock_ticket);
          hapticNotify("success");
          setValue("");
          setStep("done");
        }
      } catch (err) {
        handleApiError(err, t("security.generic_error"));
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, mode, step, t]
  );

  const onDigit = (digit) => {
    if (busy || lockSeconds > 0) return;
    const next = (value + digit).slice(0, PIN_LENGTH);
    setValue(next);
    setErrorText("");
    if (next.length === PIN_LENGTH) submit(next);
  };

  const titles = {
    current: t("security.enter_current_pin"),
    enter: mode === "change" ? t("security.new_pin") : t("security.create_pin"),
    confirm: t("security.repeat_pin"),
  };
  const subtitles = {
    current:
      mode === "disable" ? t("security.disable_pin_subtitle") : t("security.change_pin_subtitle"),
    enter: t("security.create_pin_subtitle"),
    confirm: t("security.repeat_pin_subtitle"),
  };

  // Финальный экран с предупреждением о восстановлении только через почту.
  if (step === "done") {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col bg-background" data-testid="pin-setup-done">
        <AmbientField />
        <div className="lp-layer flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10">
            <ShieldCheck className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="text-xl font-bold text-foreground">{t("security.pin_enabled_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("security.pin_enabled_warning")}</p>
          {!linkedEmail && (
            <button
              type="button"
              className="lp-press lp-tile mt-1 flex items-center gap-2 !rounded-full px-5 py-2.5 text-sm font-medium text-foreground"
              onClick={() => onLinkEmail?.()}
            >
              <Mail className="h-4 w-4 text-link" />
              {t("security.link_email_cta")}
            </button>
          )}
          <button
            type="button"
            className="mt-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground"
            onClick={() => onSuccess?.(mode === "change" ? "change" : "create")}
            data-testid="pin-setup-finish"
          >
            {t("common.done")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-background" data-testid="pin-setup-sheet">
      <AmbientField />
      <div className="lp-layer flex items-center justify-end px-4 pt-[calc(0.75rem+var(--lp-top))]">
        <button
          type="button"
          className="lp-press flex h-9 w-9 items-center justify-center rounded-full bg-[var(--lp-tile)] text-muted-foreground"
          onClick={onClose}
          aria-label={t("common.close")}
          data-testid="pin-setup-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="lp-layer flex flex-1 flex-col items-center justify-center gap-6 px-6 pb-10">
        <div className="flex flex-col items-center gap-3">
          <LightPayMark className="h-14 w-14" />
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground">{titles[step]}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{subtitles[step]}</p>
          </div>
        </div>

        <Dots filled={value.length} shaking={shaking} error={Boolean(errorText)} />

        <div className="flex h-5 items-center">
          {errorText ? (
            <p className="text-sm text-destructive-fg">{errorText}</p>
          ) : busy ? (
            <div className="spinner" />
          ) : null}
        </div>

        <PinKeypad
          onDigit={onDigit}
          onBackspace={() => setValue((v) => v.slice(0, -1))}
          disabled={busy || lockSeconds > 0}
        />
      </div>
    </div>
  );
};

export default PinSetupSheet;
